"use strict";

(() => {
const appKey = "__oryzeMorningCheckApp";

if (window[appKey]) {
  window[appKey].mount(document);
  return;
}

const initialConfig = readMorningCheckConfig(document);

// 仕様上の文言・画像・遷移先・操作感は、ここからまとめて更新できます。
const defaultQuestions = Object.freeze([
  Object.freeze({
    id: "q1",
    stateKey: "breakfast",
    text: "毎朝、朝ごはんは食べていますか？",
    image: "images/question-1.webp",
    imageAlt: "やわらかな朝の光が差す朝ごはんの食卓",
    type: "swipe",
    choices: Object.freeze([
      Object.freeze({ label: "いいえ", value: false, direction: "left" }),
      Object.freeze({ label: "はい", value: true, direction: "right" }),
    ]),
  }),
  Object.freeze({
    id: "q2",
    stateKey: "cookingBurden",
    text: "朝ごはんを作るのが面倒だと感じた経験はありますか？",
    image: "images/question-2.webp",
    imageAlt: "朝ごはんの準備をしているキッチンの手元",
    type: "swipe",
    choices: Object.freeze([
      Object.freeze({ label: "いいえ", value: false, direction: "left" }),
      Object.freeze({ label: "はい", value: true, direction: "right" }),
    ]),
  }),
  Object.freeze({
    id: "q3",
    stateKey: "healthyBreakfast",
    text: "毎朝、健康的な朝ごはんが食べられたら嬉しいと感じますか？",
    image: "images/question-3.webp",
    imageAlt: "フルーツを添えた彩りのよい朝ごはん",
    type: "swipe",
    choices: Object.freeze([
      Object.freeze({ label: "いいえ", value: false, direction: "left" }),
      Object.freeze({ label: "はい", value: true, direction: "right" }),
    ]),
  }),
  Object.freeze({
    id: "q4",
    stateKey: "mood",
    text: "今日の朝はどんな気分ですか？",
    image: "images/question-4.webp",
    imageAlt: "穏やかな朝の食卓で好みの素材を選ぶ手元",
    type: "choice",
    choices: Object.freeze([
      "すっきり始めたい",
      "元気をチャージしたい",
      "ほっと一息つきたい",
      "ちょっと贅沢したい",
    ]),
  }),
]);
let questions = Object.freeze(initialConfig?.questions || defaultQuestions);

const defaultUiCopy = Object.freeze({
  start: Object.freeze({
    logo: "",
    eyebrow: "ORYZAE MORNING TYPE CHECK",
    titleLine1: "あなたの朝ごはん",
    titleLine2: "タイプをチェック",
    description: "朝ごはんの習慣と今日の気分を、4つの質問で気軽に振り返ってみませんか？",
    heroAlt: "やわらかな朝の光が差す食卓のグラノーラボウル",
    cta: "朝ごはんタイプをチェック",
    note: "全4問・登録不要",
  }),
  progressLabel: "朝ごはんタイプチェック",
  progressAriaLabel: "質問の進み具合",
  questionLabel: "QUESTION",
  swipeHelper: "左右にスワイプで回答できます",
  choiceHelper: "いまの気分に近いものをひとつ選んでください",
  answerGroupLabel: "回答ボタン",
  result: Object.freeze({
    eyebrow: "朝ごはんタイプチェック結果",
    lead: "そんなあなたにはこちらの米麹グラノーラがおすすめ！",
    cta: "米麹グラノーラを見てみる",
  }),
});
let uiCopy = Object.freeze(initialConfig?.uiCopy || defaultUiCopy);

const defaultResultsByMood = Object.freeze({
  "すっきり始めたい": Object.freeze({
    productName: "アサイーハワイアン",
    image: "images/result-acai.webp",
    title: "すっきり軽やかな朝タイプ",
  }),
  "元気をチャージしたい": Object.freeze({
    productName: "バナナココナッツ",
    image: "images/result-banana-coconut.webp",
    title: "朝からエネルギーチャージタイプ",
  }),
  "ほっと一息つきたい": Object.freeze({
    productName: "プレーン",
    image: "images/result-plain.webp",
    title: "ほっとやさしい朝タイプ",
  }),
  "ちょっと贅沢したい": Object.freeze({
    productName: "チョコ",
    image: "images/result-chocolate.webp",
    title: "朝のちょっとごほうびタイプ",
  }),
});
let resultsByMood = Object.freeze(initialConfig?.resultsByMood || defaultResultsByMood);

const preferredBreakPositions = Object.freeze({
  "毎朝、朝ごはんは食べていますか？": Object.freeze([8]),
  "朝ごはんを作るのが面倒だと感じた経験はありますか？": Object.freeze([9, 16]),
  "毎朝、健康的な朝ごはんが食べられたら嬉しいと感じますか？": Object.freeze([12]),
  "今日の朝はどんな気分ですか？": Object.freeze([5]),
  "すっきり軽やかな朝タイプ": Object.freeze([8]),
  "朝からエネルギーチャージタイプ": Object.freeze([8]),
  "ほっとやさしい朝タイプ": Object.freeze([7]),
  "朝のちょっとごほうびタイプ": Object.freeze([6]),
});

function withPreferredBreaks(text) {
  const breakPositions = preferredBreakPositions[text];
  if (!breakPositions) return text;

  const breaks = new Set(breakPositions);
  return Array.from(text)
    .map((character, index) => character + (breaks.has(index + 1) ? "<wbr>" : ""))
    .join("");
}
const defaultDestinationUrl = "https://oryzae.shop/";
let destinationUrl = initialConfig?.destinationUrl || defaultDestinationUrl;

const resultTypeOrder = Object.freeze(["refresh", "energy", "relax", "reward"]);

function pushMorningCheckEvent(eventName, parameters = {}) {
  if (!window.dataLayer || typeof window.dataLayer.push !== "function") return;

  window.dataLayer.push({
    event: eventName,
    ...parameters,
  });
}

function getResultType(result) {
  if (result.resultType) return result.resultType;

  const moodIndex = questions[3].choices.indexOf(answerState.mood);
  return resultTypeOrder[moodIndex] || "";
}

function buildTrackedDestinationUrl(destination, resultType) {
  const url = new URL(destination, window.location.href);
  const currentSearchParams = new URLSearchParams(window.location.search);
  const currentKeys = new Set(currentSearchParams.keys());

  currentKeys.forEach((key) => url.searchParams.delete(key));
  currentSearchParams.forEach((value, key) => url.searchParams.append(key, value));

  url.searchParams.set("bc_type", resultType);
  url.searchParams.set("bc_q1", answerState.breakfast ? "y" : "n");
  url.searchParams.set("bc_q2", answerState.cookingBurden ? "y" : "n");
  url.searchParams.set("bc_q3", answerState.healthyBreakfast ? "y" : "n");

  return url.toString();
}

const animationSettings = Object.freeze({
  screenEnterDuration: 300,
  screenEnterDistance: 8,
  cardEnterDuration: 280,
  cardEnterDistance: 14,
  cardEnterScale: 0.975,
  progressDuration: 300,
  interactionDuration: 160,
  hoverLift: -1,
  choiceHoverShift: 2,
  initialSwipeHintDuration: 800,
  initialSwipeHintDistance: 8,
  pressScale: 0.98,
  resultEnterDistance: 8,
  resultCheckingDuration: 1500,
  resultRevealDuration: 1100,
  flyOutDuration: 300,
  returnDuration: 200,
  maxRotation: 8,
  flyOutDistanceMultiplier: 1.35,
  flyOutVerticalOffset: -10,
});

// 距離と速度の複合判定。Phase 5の実機調整はこの3定数を更新します。
const swipeThreshold = 0.2;
const flickVelocityThreshold = 0.5;
const flickVelocitySampleWindow = 100;

// 回答はメモリ内だけに保持し、外部送信・Web Storageへの保存は行いません。
const answerState = {
  breakfast: null,
  cookingBurden: null,
  healthyBreakfast: null,
  mood: null,
};

let screenRoot = null;
let screenAnnouncer = null;
const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

let currentQuestionIndex = -1;
let currentScreen = "start";
let isTransitioning = false;
let hasPlayedInitialSwipeHint = false;
let hasMounted = false;
let announceFrameId = 0;
let startLayoutFrameId = 0;
const scheduledTimeoutIds = new Set();

const startLayoutSettings = Object.freeze({
  compactMaxViewportHeight: 667,
  standardMinimumAspectRatio: 16 / 9,
  compactMinimumAspectRatio: 21 / 9,
  maximumAspectRatio: 4 / 3,
});

function scheduleTimeout(callback, delay) {
  const timeoutId = window.setTimeout(() => {
    scheduledTimeoutIds.delete(timeoutId);
    callback();
  }, delay);

  scheduledTimeoutIds.add(timeoutId);
  return timeoutId;
}

function clearScheduledTimeout(timeoutId) {
  if (!timeoutId) return;
  window.clearTimeout(timeoutId);
  scheduledTimeoutIds.delete(timeoutId);
}

function clearScheduledTimeouts() {
  scheduledTimeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
  scheduledTimeoutIds.clear();
}

function getDuration(duration) {
  return reducedMotionQuery.matches ? 1 : duration;
}

function applyAnimationSettings() {
  const rootStyle = document.documentElement.style;
  rootStyle.setProperty("--screen-enter-duration", animationSettings.screenEnterDuration + "ms");
  rootStyle.setProperty("--screen-enter-distance", animationSettings.screenEnterDistance + "px");
  rootStyle.setProperty("--card-enter-duration", animationSettings.cardEnterDuration + "ms");
  rootStyle.setProperty("--card-enter-distance", animationSettings.cardEnterDistance + "px");
  rootStyle.setProperty("--card-enter-scale", String(animationSettings.cardEnterScale));
  rootStyle.setProperty("--progress-duration", animationSettings.progressDuration + "ms");
  rootStyle.setProperty("--interaction-duration", animationSettings.interactionDuration + "ms");
  rootStyle.setProperty("--hover-lift", animationSettings.hoverLift + "px");
  rootStyle.setProperty("--choice-hover-shift", animationSettings.choiceHoverShift + "px");
  rootStyle.setProperty("--initial-swipe-hint-duration", animationSettings.initialSwipeHintDuration + "ms");
  rootStyle.setProperty("--initial-swipe-hint-distance", animationSettings.initialSwipeHintDistance + "px");
  rootStyle.setProperty("--press-scale", String(animationSettings.pressScale));
  rootStyle.setProperty("--result-enter-distance", animationSettings.resultEnterDistance + "px");
  rootStyle.setProperty("--result-checking-duration", animationSettings.resultCheckingDuration + "ms");
  rootStyle.setProperty("--result-reveal-duration", animationSettings.resultRevealDuration + "ms");
}

function announce(message) {
  const announcer = screenAnnouncer;
  if (!announcer) return;

  announcer.textContent = "";
  window.cancelAnimationFrame(announceFrameId);
  announceFrameId = window.requestAnimationFrame(() => {
    announceFrameId = 0;
    if (announcer.isConnected) announcer.textContent = message;
  });
}

function focusScreenTitle() {
  window.requestAnimationFrame(() => {
    const title = document.querySelector("#screen-title");
    if (title) {
      title.focus({ preventScroll: true });
    }
  });
}

function updateStartHeroSize() {
  const startScreen = document.querySelector(".start-screen");
  const startPanel = startScreen?.querySelector(".start-panel");
  const heroFrame = startPanel?.querySelector(".hero-frame");
  if (!startScreen || !startPanel || !heroFrame) return;

  const screenStyle = window.getComputedStyle(startScreen);
  const verticalPadding =
    Number.parseFloat(screenStyle.paddingTop) + Number.parseFloat(screenStyle.paddingBottom);
  const availablePanelHeight = startScreen.clientHeight - verticalPadding;
  const panelHeight = startPanel.getBoundingClientRect().height;
  const heroRect = heroFrame.getBoundingClientRect();
  const fixedPanelHeight = panelHeight - heroRect.height;
  const heroWidth = heroRect.width;
  if (heroWidth <= 0 || availablePanelHeight <= 0) return;

  const isCompact = startScreen.clientHeight <= startLayoutSettings.compactMaxViewportHeight;
  const minimumAspectRatio = isCompact
    ? startLayoutSettings.compactMinimumAspectRatio
    : startLayoutSettings.standardMinimumAspectRatio;
  const minimumHeroHeight = heroWidth / minimumAspectRatio;
  const maximumHeroHeight = heroWidth / startLayoutSettings.maximumAspectRatio;
  const desiredHeroHeight = availablePanelHeight - fixedPanelHeight;
  const heroHeight = Math.min(
    maximumHeroHeight,
    Math.max(minimumHeroHeight, desiredHeroHeight),
  );

  heroFrame.style.height = heroHeight + "px";
  heroFrame.style.setProperty("--start-hero-ratio", `${heroWidth} / ${heroHeight}`);
}

function scheduleStartHeroSizeUpdate() {
  window.cancelAnimationFrame(startLayoutFrameId);
  startLayoutFrameId = window.requestAnimationFrame(updateStartHeroSize);
}

function renderStartScreen() {
  currentScreen = "start";
  currentQuestionIndex = -1;
  isTransitioning = false;

  const startLogoMarkup = uiCopy.start.logo
    ? `<img class="brand-logo" src="${uiCopy.start.logo}" alt="" width="42" height="42">`
    : `<span class="brand-symbol" aria-hidden="true"></span>`;

  const startDescriptionBreakText = "4つの質問で";
  const startDescriptionBreakIndex = uiCopy.start.description.indexOf(startDescriptionBreakText);
  const startDescriptionMarkup =
    startDescriptionBreakIndex > -1
      ? `<span class="start-description-line">${uiCopy.start.description.slice(0, startDescriptionBreakIndex)}</span><br><span class="start-description-line">${uiCopy.start.description.slice(startDescriptionBreakIndex)}</span>`
      : uiCopy.start.description;

  screenRoot.innerHTML = `
    <section class="screen start-screen">
      <div class="start-panel">
        <div class="start-copy">
          ${startLogoMarkup}
          <p class="eyebrow">${uiCopy.start.eyebrow}</p>
          <h1 id="screen-title" tabindex="-1">
            <span class="start-title-line">${uiCopy.start.titleLine1}</span>
            <span class="start-title-line">${uiCopy.start.titleLine2}</span>
          </h1>
          <p class="start-description">
            ${startDescriptionMarkup}
          </p>
        </div>
        <figure class="hero-frame">
          <img
            src="${uiCopy.start.image || "images/hero.webp"}"
            alt="${uiCopy.start.heroAlt}"
            width="1024"
            height="682"
          >
        </figure>
        <button id="start-button" class="primary-button" type="button">
          ${uiCopy.start.cta}
        </button>
        <p class="start-note">${uiCopy.start.note}</p>
      </div>
    </section>
  `;

  document.querySelector("#start-button").addEventListener("click", () => {
    if (isTransitioning) return;
    pushMorningCheckEvent("check_start");
    isTransitioning = true;
    renderQuestion(0);
  });

  updateStartHeroSize();
  focusScreenTitle();
}

function progressMarkup(questionNumber) {
  const progress = (questionNumber / questions.length) * 100;
  const previousProgress = ((questionNumber - 1) / questions.length) * 100;

  return `
    <header class="quiz-header">
      <div class="progress-copy">
        <span>${uiCopy.progressLabel}</span>
        <strong>${questionNumber}問目 / ${questions.length}問中</strong>
      </div>
      <div
        class="progress-track"
        role="progressbar"
        aria-label="${uiCopy.progressAriaLabel}"
        aria-valuemin="1"
        aria-valuemax="${questions.length}"
        aria-valuenow="${questionNumber}"
        aria-valuetext="${questions.length}問中${questionNumber}問目"
      >
        <div
          class="progress-fill"
          data-progress-target="${progress}"
          style="width: ${previousProgress}%"
        ></div>
      </div>
    </header>
  `;
}

function animateProgressBar() {
  const progressFill = screenRoot.querySelector(".progress-fill");
  if (!progressFill) return;

  const targetProgress = progressFill.dataset.progressTarget;
  progressFill.getBoundingClientRect();
  window.requestAnimationFrame(() => {
    progressFill.style.width = targetProgress + "%";
  });
}

function cardStackMarkup(cardMarkup, isChoice = false) {
  return `
    <div class="card-stack${isChoice ? " card-stack--choice" : ""}">
      <div class="card-back card-back--far" aria-hidden="true"></div>
      <div class="card-back card-back--near" aria-hidden="true"></div>
      ${cardMarkup}
    </div>
  `;
}

function questionImageMarkup(question) {
  return `
    <figure class="question-figure">
      <img
        src="${question.image}"
        alt="${question.imageAlt}"
        width="1024"
        height="576"
        draggable="false"
      >
    </figure>
  `;
}

function swipeQuestionMarkup(question, questionNumber) {
  return cardStackMarkup(`
    <article
      id="question-card"
      class="question-card question-card--swipe"
      aria-describedby="swipe-instruction"
    >
      <span id="swipe-label-yes" class="swipe-label swipe-label--yes" aria-hidden="true">${question.choices[1].label}</span>
      <span id="swipe-label-no" class="swipe-label swipe-label--no" aria-hidden="true">${question.choices[0].label}</span>
      <p class="question-number">${uiCopy.questionLabel} ${questionNumber}</p>
      ${questionImageMarkup(question)}
      <div class="question-content">
        <h1 id="screen-title" tabindex="-1">${withPreferredBreaks(question.text)}</h1>
      </div>
      <p id="swipe-instruction" class="sr-only">${question.swipeHelper || uiCopy.swipeHelper}</p>
      <p class="direction-guide" aria-hidden="true">${question.swipeHelper || uiCopy.swipeHelper}</p>
    </article>
  `);
}

function choiceQuestionMarkup(question, questionNumber) {
  const options = question.choices
    .map(
      (choice) => `
        <button class="mood-button" type="button" data-mood="${choice}">
          <span>${choice}</span>
        </button>
      `,
    )
    .join("");

  return cardStackMarkup(
    `
      <article
        id="question-card"
        class="question-card question-card--choice"
        aria-describedby="choice-instruction"
      >
        <p class="question-number">${uiCopy.questionLabel} ${questionNumber}</p>
        ${questionImageMarkup(question)}
        <div class="choice-question-copy">
          <h1 id="screen-title" tabindex="-1">${withPreferredBreaks(question.text)}</h1>
        </div>
        <p id="choice-instruction" class="sr-only">${uiCopy.choiceHelper}</p>
        <div class="mood-options">${options}</div>
      </article>
    `,
    true,
  );
}

function renderQuestion(index) {
  const question = questions[index];
  if (!question) return;

  currentScreen = "question";
  currentQuestionIndex = index;
  isTransitioning = false;

  const questionNumber = index + 1;
  const cardMarkup =
    question.type === "swipe"
      ? swipeQuestionMarkup(question, questionNumber)
      : choiceQuestionMarkup(question, questionNumber);

  screenRoot.innerHTML = `
    <section class="screen quiz-screen quiz-screen--${question.type}">
      ${progressMarkup(questionNumber)}
      <div class="quiz-body quiz-body--${question.type}">
        ${cardMarkup}
        ${
          question.type === "swipe"
            ? `
              <div class="binary-actions" aria-label="${uiCopy.answerGroupLabel}">
                <button class="answer-button" type="button" data-answer="no">${question.choices[0].label}</button>
                <button class="answer-button answer-button--yes" type="button" data-answer="yes">${question.choices[1].label}</button>
              </div>
            `
            : ""
        }
      </div>
    </section>
  `;

  animateProgressBar();

  if (question.type === "swipe") {
    const card = document.querySelector("#question-card");
    if (index === 0 && !hasPlayedInitialSwipeHint && card) {
      card.classList.add("is-swipe-hinting");
      hasPlayedInitialSwipeHint = true;
    }

    setupSwipeInteraction(question);
    setupAnswerButtons(question);
  } else {
    setupMoodButtons(question);
  }

  announce(`${questions.length}問中${questionNumber}問目。${question.text}`);
  focusScreenTitle();
}

function setupAnswerButtons(question) {
  const buttons = document.querySelectorAll("[data-answer]");

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      if (isTransitioning) return;

      const isYes = button.dataset.answer === "yes";
      commitBinaryAnswer(question, isYes, isYes ? 1 : -1);
    });
  });
}

function setupSwipeInteraction(question) {
  const card = document.querySelector("#question-card");
  const yesLabel = document.querySelector("#swipe-label-yes");
  const noLabel = document.querySelector("#swipe-label-no");

  let activePointerId = null;
  let startX = 0;
  let startY = 0;
  let deltaX = 0;
  let cardWidth = 1;
  let gestureAxis = "pending";
  let committed = false;
  let velocitySamples = [];

  const recordVelocitySample = (x, time) => {
    velocitySamples.push({ x, time });
    const cutoff = time - flickVelocitySampleWindow;

    while (velocitySamples.length > 2 && velocitySamples[1].time <= cutoff) {
      velocitySamples.shift();
    }
  };

  const getHorizontalVelocity = () => {
    if (velocitySamples.length < 2) return 0;

    const firstSample = velocitySamples[0];
    const lastSample = velocitySamples[velocitySamples.length - 1];
    const elapsed = lastSample.time - firstSample.time;

    return elapsed > 0 ? (lastSample.x - firstSample.x) / elapsed : 0;
  };

  const clearGesture = () => {
    activePointerId = null;
    deltaX = 0;
    gestureAxis = "pending";
    velocitySamples = [];
    card.classList.remove("is-dragging");
  };

  const resetCard = (animate = true) => {
    if (committed || isTransitioning) return;

    const duration = animate ? getDuration(animationSettings.returnDuration) : 0;
    card.style.transition = `transform ${duration}ms var(--ease-out), opacity ${duration}ms ease`;
    card.style.transform = "translate3d(0, 0, 0) rotate(0deg)";
    card.style.opacity = "1";
    yesLabel.style.opacity = "0";
    noLabel.style.opacity = "0";
    clearGesture();

    scheduleTimeout(() => {
      if (!isTransitioning) card.style.transition = "";
    }, duration);
  };

  card.addEventListener("pointerdown", (event) => {
    if (isTransitioning || activePointerId !== null || event.isPrimary === false) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    card.classList.remove("is-swipe-hinting");
    activePointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    cardWidth = Math.max(card.offsetWidth, 1);
    deltaX = 0;
    gestureAxis = "pending";
    committed = false;
    velocitySamples = [];
    recordVelocitySample(event.clientX, event.timeStamp);
    card.style.transition = "none";
    card.classList.add("is-dragging");
    card.setPointerCapture(event.pointerId);
  });

  card.addEventListener("pointermove", (event) => {
    if (event.pointerId !== activePointerId || isTransitioning) return;

    const moveX = event.clientX - startX;
    const moveY = event.clientY - startY;

    if (gestureAxis === "pending" && Math.max(Math.abs(moveX), Math.abs(moveY)) >= 7) {
      gestureAxis = Math.abs(moveX) > Math.abs(moveY) ? "horizontal" : "vertical";
    }

    if (gestureAxis !== "horizontal") return;

    if (event.cancelable) event.preventDefault();
    deltaX = moveX;
    cardWidth = Math.max(card.offsetWidth, 1);
    card.style.transition = "none";
    recordVelocitySample(event.clientX, event.timeStamp);

    const rotation = Math.max(
      -animationSettings.maxRotation,
      Math.min(animationSettings.maxRotation, (deltaX / cardWidth) * animationSettings.maxRotation),
    );
    const labelOpacity = Math.min(Math.abs(deltaX) / (cardWidth * swipeThreshold), 1);

    card.style.transform = `translate3d(${deltaX}px, 0, 0) rotate(${rotation}deg)`;
    yesLabel.style.opacity = deltaX > 0 ? String(labelOpacity) : "0";
    noLabel.style.opacity = deltaX < 0 ? String(labelOpacity) : "0";
  });

  card.addEventListener("pointerup", (event) => {
    if (event.pointerId !== activePointerId || isTransitioning) return;

    if (gestureAxis === "horizontal") {
      deltaX = event.clientX - startX;
      recordVelocitySample(event.clientX, event.timeStamp);
    }

    const thresholdInPixels = cardWidth * swipeThreshold;
    const horizontalVelocity = getHorizontalVelocity();
    const passedDistanceThreshold = Math.abs(deltaX) >= thresholdInPixels;
    const passedVelocityThreshold = Math.abs(horizontalVelocity) >= flickVelocityThreshold;
    const shouldCommit =
      gestureAxis === "horizontal" && (passedDistanceThreshold || passedVelocityThreshold);

    if (shouldCommit) {
      const directionValue = passedDistanceThreshold ? deltaX : horizontalVelocity;
      const isYes = directionValue > 0;
      committed = true;
      clearGesture();
      commitBinaryAnswer(question, isYes, isYes ? 1 : -1);
      return;
    }

    resetCard(true);
  });

  card.addEventListener("pointercancel", (event) => {
    if (event.pointerId === activePointerId) resetCard(true);
  });
  card.addEventListener("lostpointercapture", () => {
    if (activePointerId !== null && !committed) resetCard(true);
  });

  card.addEventListener("dragstart", (event) => event.preventDefault());
}

function commitBinaryAnswer(question, value, direction) {
  if (isTransitioning) return;

  const card = document.querySelector("#question-card");
  if (card) card.classList.remove("is-swipe-hinting");

  isTransitioning = true;
  answerState[question.stateKey] = value;
  pushMorningCheckEvent("check_answer", {
    question_id: question.id,
    answer: value ? "y" : "n",
  });
  disableCurrentControls();
  flyOutCurrentCard(direction, () => renderQuestion(currentQuestionIndex + 1));
}

function setupMoodButtons(question) {
  const buttons = document.querySelectorAll("[data-mood]");

  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      if (isTransitioning) return;

      isTransitioning = true;
      answerState[question.stateKey] = button.dataset.mood;
      pushMorningCheckEvent("check_answer", {
        question_id: question.id,
        answer: button.dataset.mood,
      });
      button.classList.add("is-selected");
      disableCurrentControls();

      transitionToResult();
    });
  });
}

function disableCurrentControls() {
  document.querySelectorAll("button").forEach((button) => {
    button.disabled = true;
  });
}

function transitionToResult() {
  if (reducedMotionQuery.matches) {
    beginResultCheck();
    return;
  }

  flyOutCurrentCard(1, beginResultCheck);
}

function flyOutCurrentCard(direction, onComplete) {
  const card = document.querySelector("#question-card");
  if (!card) return;

  const duration = getDuration(animationSettings.flyOutDuration);
  const viewportDistance = Math.max(window.innerWidth, card.getBoundingClientRect().width);
  const distance = viewportDistance * animationSettings.flyOutDistanceMultiplier * direction;

  card.style.pointerEvents = "none";
  card.style.transition = `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${duration}ms ease`;

  window.requestAnimationFrame(() => {
    card.style.transform = `translate3d(${distance}px, ${animationSettings.flyOutVerticalOffset}px, 0) rotate(${direction * animationSettings.maxRotation}deg)`;
    card.style.opacity = "0";
  });

  scheduleTimeout(onComplete, duration);
}

const resultLayoutSettings = Object.freeze({
  compactMaxViewportWidth: 360,
  compactMaxViewportHeight: 667,
  standardMinimumAspectRatio: 16 / 9,
  compactMinimumAspectRatio: 21 / 9,
  maximumAspectRatio: 4 / 3,
});

let resultLayoutFrameId = 0;
let resultCheckTimerId = 0;

function renderResultCheckingScreen() {
  currentScreen = "checking";
  screenRoot.innerHTML = `
    <section class="screen result-checking-screen">
      <div class="result-checking-content">
        <p id="screen-title" class="result-checking-text" role="status" tabindex="-1">
          ${uiCopy.result.checking || "あなたの朝ごはんタイプを判定中…"}
        </p>
        <div class="result-loading-track" aria-hidden="true">
          <span class="result-loading-bar"></span>
        </div>
      </div>
    </section>
  `;

  announce((uiCopy.result.checking || "あなたの朝ごはんタイプを判定中…").replace(/…$/, ""));
  focusScreenTitle();
}

function beginResultCheck() {
  if (!isTransitioning || !answerState.mood || resultCheckTimerId !== 0) return;

  if (reducedMotionQuery.matches) {
    renderResult();
    return;
  }

  renderResultCheckingScreen();
  resultCheckTimerId = scheduleTimeout(() => {
    resultCheckTimerId = 0;
    if (isTransitioning) renderResult();
  }, animationSettings.resultCheckingDuration);
}

function updateResultImageSize() {
  const resultScreen = document.querySelector(".result-screen");
  const resultCard = resultScreen?.querySelector(".result-card");
  const resultImage = resultCard?.querySelector(".result-image");
  if (!resultScreen || !resultCard || !resultImage) return;

  const screenStyle = window.getComputedStyle(resultScreen);
  const verticalPadding =
    Number.parseFloat(screenStyle.paddingTop) + Number.parseFloat(screenStyle.paddingBottom);
  const availableCardHeight = resultScreen.clientHeight - verticalPadding;
  const cardHeight = resultCard.getBoundingClientRect().height;
  const imageRect = resultImage.getBoundingClientRect();
  const fixedCardHeight = cardHeight - imageRect.height;
  const imageWidth = imageRect.width;
  if (imageWidth <= 0 || availableCardHeight <= 0) return;

  const isCompact =
    Math.round(window.innerWidth) <= resultLayoutSettings.compactMaxViewportWidth ||
    Math.round(window.innerHeight) <= resultLayoutSettings.compactMaxViewportHeight;
  const minimumAspectRatio = isCompact
    ? resultLayoutSettings.compactMinimumAspectRatio
    : resultLayoutSettings.standardMinimumAspectRatio;
  const minimumImageHeight = imageWidth / minimumAspectRatio;
  const maximumImageHeight = imageWidth / resultLayoutSettings.maximumAspectRatio;
  const desiredImageHeight = availableCardHeight - fixedCardHeight;
  const imageHeight = Math.min(
    maximumImageHeight,
    Math.max(minimumImageHeight, desiredImageHeight),
  );

  resultImage.style.height = imageHeight + "px";
  resultImage.style.aspectRatio = `${imageWidth} / ${imageHeight}`;
}

function scheduleResultImageSizeUpdate() {
  window.cancelAnimationFrame(resultLayoutFrameId);
  resultLayoutFrameId = window.requestAnimationFrame(updateResultImageSize);
}

function renderResult({ track = true } = {}) {
  if (resultCheckTimerId !== 0) {
    clearScheduledTimeout(resultCheckTimerId);
    resultCheckTimerId = 0;
  }

  const result = resultsByMood[answerState.mood];
  if (!result) {
    renderStartScreen();
    return;
  }
  const resultType = getResultType(result);

  currentScreen = "result";
  isTransitioning = false;

  screenRoot.innerHTML = `
    <section class="screen result-screen">
      <article class="result-card">
        <p class="result-eyebrow">${uiCopy.result.eyebrow}</p>
        <h1 id="screen-title" tabindex="-1">${withPreferredBreaks(result.title)}</h1>
        <p class="result-lead">${uiCopy.result.lead}</p>
        <img
          class="result-image"
          src="${result.image}"
          alt="${result.productName}の米麹グラノーラのイメージ"
          width="1024"
          height="576"
        >
        <h2 class="result-product-name">${result.productName}</h2>
        <a class="result-cta" href="${result.destinationUrl || destinationUrl}">${uiCopy.result.cta}</a>
      </article>
    </section>
  `;

  if (track) {
    pushMorningCheckEvent("check_complete", {
      result_type: resultType,
    });
  }

  const resultCta = screenRoot.querySelector(".result-cta");
  resultCta?.addEventListener("click", () => {
    resultCta.href = buildTrackedDestinationUrl(resultCta.href, resultType);
    pushMorningCheckEvent("check_cta_click", {
      result_type: resultType,
    });
  });

  const resultCard = screenRoot.querySelector(".result-card");
  if (!reducedMotionQuery.matches) resultCard?.classList.add("is-revealing");

  updateResultImageSize();
  announce(`朝ごはんタイプチェック結果。${result.title}。${result.productName}がおすすめです。`);
  focusScreenTitle();
}

function readMorningCheckConfig(scope) {
  const configElement = scope.querySelector?.("[data-oryze-morning-check-config]");
  if (!configElement) return window.OryzeMorningCheckConfig || {};

  try {
    return JSON.parse(configElement.textContent);
  } catch (error) {
    console.error("Morning check config could not be parsed.", error);
    return window.OryzeMorningCheckConfig || {};
  }
}

function applyMorningCheckConfig(config) {
  questions = Object.freeze(config.questions || defaultQuestions);
  uiCopy = Object.freeze(config.uiCopy || defaultUiCopy);
  resultsByMood = Object.freeze(config.resultsByMood || defaultResultsByMood);
  destinationUrl = config.destinationUrl || defaultDestinationUrl;
  window.OryzeMorningCheckConfig = config;
}

function cleanupMorningCheckAsync() {
  clearScheduledTimeouts();
  resultCheckTimerId = 0;

  window.cancelAnimationFrame(announceFrameId);
  window.cancelAnimationFrame(startLayoutFrameId);
  window.cancelAnimationFrame(resultLayoutFrameId);
  announceFrameId = 0;
  startLayoutFrameId = 0;
  resultLayoutFrameId = 0;
}

function resetMorningCheckState() {
  cleanupMorningCheckAsync();

  Object.assign(answerState, {
    breakfast: null,
    cookingBurden: null,
    healthyBreakfast: null,
    mood: null,
  });
  currentQuestionIndex = -1;
  currentScreen = "start";
  isTransitioning = false;
  hasPlayedInitialSwipeHint = false;
}

let resizeListenersAttached = false;

function attachResizeListeners() {
  if (resizeListenersAttached) return;
  window.addEventListener("resize", scheduleStartHeroSizeUpdate);
  window.addEventListener("resize", scheduleResultImageSizeUpdate);
  resizeListenersAttached = true;
}

function detachResizeListeners() {
  if (!resizeListenersAttached) return;
  window.removeEventListener("resize", scheduleStartHeroSizeUpdate);
  window.removeEventListener("resize", scheduleResultImageSizeUpdate);
  resizeListenersAttached = false;
}

function restoreMorningCheckScreen() {
  isTransitioning = false;

  if (currentScreen === "question" && questions[currentQuestionIndex]) {
    renderQuestion(currentQuestionIndex);
    return;
  }

  if (currentScreen === "checking" && resultsByMood[answerState.mood]) {
    isTransitioning = true;
    beginResultCheck();
    return;
  }

  if (currentScreen === "result" && resultsByMood[answerState.mood]) {
    renderResult({ track: false });
    return;
  }

  renderStartScreen();
}

function mountMorningCheck(scope) {
  const nextScreenRoot = scope.querySelector?.("#screen-root");
  const nextScreenAnnouncer = scope.querySelector?.("#screen-announcer");
  if (!nextScreenRoot || !nextScreenAnnouncer) return;

  const config = readMorningCheckConfig(scope);
  if (hasMounted) {
    cleanupMorningCheckAsync();
  } else {
    resetMorningCheckState();
  }
  applyMorningCheckConfig(config);
  screenRoot = nextScreenRoot;
  screenAnnouncer = nextScreenAnnouncer;
  attachResizeListeners();
  applyAnimationSettings();
  if (hasMounted) {
    restoreMorningCheckScreen();
  } else {
    renderStartScreen();
    hasMounted = true;
  }
}

function unmountMorningCheck(scope) {
  if (!scope.querySelector?.("#screen-root")) return;

  cleanupMorningCheckAsync();
  detachResizeListeners();
  screenRoot = null;
  screenAnnouncer = null;
}

window[appKey] = {
  mount: mountMorningCheck,
  unmount: unmountMorningCheck,
};

mountMorningCheck(document);

if (window.Shopify?.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    window[appKey].mount(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    window[appKey].unmount(event.target);
  });
}
})();
